import { isSameDay } from "date-fns";

import { Button } from "@/components/ui/button";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";

const SKELETON_ROWS = ["alpha", "bravo", "charlie", "delta"];

const formatCurrency = (value: number) =>
	new Intl.NumberFormat("pt-PT", {
		style: "currency",
		currency: "EUR",
		maximumFractionDigits: 2,
	}).format(value);

const normalizeNumber = (value: number | null | undefined) => {
	const parsed = Number(value ?? 0);
	return Number.isNaN(parsed) ? 0 : parsed;
};

type EquipmentSummary = {
	brand?: string | null;
	model?: string | null;
};

type ClientSummary = {
	name?: string | null;
	createdAt?: string | Date | null;
};

type RepairEntry = {
	id?: string | number | null;
	repairNumber?: string | number | null;
	createdAt?: string | Date | null;
	client?: ClientSummary | null;
	clientEquipment?: EquipmentSummary | null;
	problemDescription?: string | null;
	problemName?: string | null;
	storeBox?: string | null;
	quotation?: number | null;
	priority?: string | null;
	complaint?: boolean | null;
};

type RepairWaitAttributionTableProps = {
	repairs: RepairEntry[];
	loading?: boolean;
	selectedTechnician?: { name?: string | null } | null;
	onView: (repair: RepairEntry) => void;
	onAssignTechnician: (repair: RepairEntry) => void;
	onRequestMaterial: (repair: RepairEntry) => void;
	onWarranty: (repair: RepairEntry) => void;
};

const buildEquipmentLabel = (equipment?: EquipmentSummary | null) => {
	if (!equipment) {
		return "-";
	}

	const brand = equipment.brand?.trim() ?? "";
	const model = equipment.model?.trim() ?? "";
	const label = `${brand} ${model}`.trim();
	return label.length > 0 ? label : "-";
};

const resolveProblem = (repair: RepairEntry) =>
	repair.problemDescription || repair.problemName || "-";

const isNewClient = (repair: RepairEntry) => {
	const clientDate = repair.client?.createdAt;
	const repairDate = repair.createdAt;

	if (!clientDate || !repairDate) {
		return false;
	}

	return isSameDay(new Date(clientDate), new Date(repairDate));
};

export function RepairWaitAttributionTable({
	repairs,
	loading = false,
	onView,
	onAssignTechnician,
	onRequestMaterial,
	onWarranty,
}: RepairWaitAttributionTableProps) {
	const renderSkeleton = () => (
		<TableBody>
			{SKELETON_ROWS.map((rowKey) => (
				<TableRow
					key={`wait-attr-skeleton-${rowKey}`}
					className="animate-pulse"
				>
					<TableCell>
						<div className="h-4 w-16 rounded bg-muted/70" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-32 rounded bg-muted/60" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-32 rounded bg-muted/50" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-32 rounded bg-muted/40" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-20 rounded bg-muted/40" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-24 rounded bg-muted/40" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-20 rounded bg-muted/30" />
					</TableCell>
					<TableCell className="text-right">
						<div className="ml-auto h-8 w-52 rounded bg-muted/30" />
					</TableCell>
				</TableRow>
			))}
		</TableBody>
	);

	const renderEmpty = () => (
		<TableBody>
			<TableRow>
				<TableCell
					colSpan={8}
					className="py-10 text-center text-sm text-muted-foreground"
				>
					Sem reparações para atribuir.
				</TableCell>
			</TableRow>
		</TableBody>
	);

	return (
		<div className="overflow-hidden rounded-lg border border-border/60 bg-card/70">
			<Table className="tp-Screening__table">
				<TableHeader>
					<TableRow className="bg-muted/50">
						<TableHead className="w-[70px]">#</TableHead>
						<TableHead className="w-[200px]">Cliente</TableHead>
						<TableHead className="w-[200px]">Equipamento</TableHead>
						<TableHead className="w-[200px]">Problema</TableHead>
						<TableHead className="w-[70px] text-center">Caixa</TableHead>
						<TableHead className="w-[120px] text-center">Orçamento</TableHead>
						<TableHead className="w-[120px] text-center">Prioridade</TableHead>
						<TableHead className="w-[240px] text-right" />
					</TableRow>
				</TableHeader>
				{loading ? (
					renderSkeleton()
				) : repairs.length > 0 ? (
					<TableBody>
						{repairs.map((repair) => {
							const rowKey = repair.id ?? repair.repairNumber ?? "repair-row";

							return (
								<TableRow key={rowKey}>
									<TableCell>{repair.repairNumber ?? "-"}</TableCell>
									<TableCell>
										<div className="tp-Screening-client">
											<span className="tp-Screening-client__name">
												{isNewClient(repair) ? (
													<span
														title="Novo cliente"
														className="tp-Screening-client__circle"
													/>
												) : null}
												{repair.complaint ? (
													<span
														title="Reclamação"
														className="tp-Screening-complaint__circle"
													/>
												) : null}
												{repair.client?.name ?? "-"}
											</span>
										</div>
									</TableCell>
									<TableCell>
										{buildEquipmentLabel(repair.clientEquipment)}
									</TableCell>
									<TableCell>{resolveProblem(repair)}</TableCell>
									<TableCell className="text-center">
										{repair.storeBox ?? "-"}
									</TableCell>
									<TableCell className="text-center">
										{formatCurrency(normalizeNumber(repair.quotation))}
									</TableCell>
									<TableCell className="text-center">
										{repair.priority ?? "-"}
									</TableCell>
									<TableCell className="text-right">
										<div className="flex justify-end gap-2">
											<Button
												size="sm"
												variant="secondary"
												onClick={() => onView(repair)}
											>
												Ver
											</Button>
											<Button
												size="sm"
												variant="secondary"
												onClick={() => onAssignTechnician(repair)}
											>
												Atribuir
											</Button>
											<Button
												size="sm"
												variant="secondary"
												onClick={() => onRequestMaterial(repair)}
											>
												Material
											</Button>
											<Button
												size="sm"
												variant="secondary"
												onClick={() => onWarranty(repair)}
											>
												Garantia
											</Button>
										</div>
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				) : (
					renderEmpty()
				)}
			</Table>
		</div>
	);
}
